/**
 * Platform Detection Utilities
 *
 * Provides functions to detect user's platform (iOS, Android, Desktop),
 * browser information, and WebAuthn API support.
 *
 * @module utils/platform
 */

/**
 * Supported platform types
 */
export type Platform = 'iOS' | 'Android' | 'Desktop' | 'Unknown';

/**
 * Browser information including name, version, and detected platform
 */
export interface BrowserInfo {
  /**
   * Browser name (e.g., 'Chrome', 'Safari', 'Firefox')
   */
  name: string;

  /**
   * Browser version string
   */
  version: string;

  /**
   * Detected platform
   */
  platform: Platform;
}

/**
 * Detects the user's platform based on the user agent string
 *
 * This function checks the navigator.userAgent and navigator.vendor
 * to determine if the user is on iOS, Android, Desktop, or Unknown platform.
 *
 * @returns The detected platform type
 *
 * @example
 * ```typescript
 * const platform = detectPlatform();
 * if (platform === 'iOS') {
 *   // iOS-specific logic
 * }
 * ```
 */
export function detectPlatform(): Platform {
  // Check if we're in a browser environment
  if (typeof navigator === 'undefined') {
    return 'Unknown';
  }

  const ua = navigator.userAgent || (navigator as any).vendor || '';

  // Check for Android
  if (/android/i.test(ua)) {
    return 'Android';
  }

  // Check for iOS
  // Note: MSStream check is for IE11 compatibility
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
    return 'iOS';
  }

  // Default to Desktop if we have a navigator
  if (typeof navigator !== 'undefined') {
    return 'Desktop';
  }

  return 'Unknown';
}

/**
 * Extracts detailed browser information including name, version, and platform
 *
 * This function parses the user agent string to determine the browser
 * name and version. It supports detection of major browsers including
 * Chrome, Safari, Firefox, Edge, and Opera.
 *
 * @returns Browser information object
 *
 * @example
 * ```typescript
 * const info = getBrowserInfo();
 * console.log(`${info.name} ${info.version} on ${info.platform}`);
 * // Output: "Chrome 120.0 on Desktop"
 * ```
 */
export function getBrowserInfo(): BrowserInfo {
  const platform = detectPlatform();

  // Default values
  let name = 'Unknown';
  let version = 'Unknown';

  if (typeof navigator === 'undefined') {
    return { name, version, platform };
  }

  const ua = navigator.userAgent;

  // Detect browser name and version
  // Order matters: check for more specific browsers first

  // Opera (must check before Chrome)
  if (/OPR\//.test(ua) || /Opera\//.test(ua)) {
    name = 'Opera';
    const oprMatch = ua.match(/OPR\/([\d.]+)/);
    const operaMatch = ua.match(/Opera\/([\d.]+)/);
    version = oprMatch ? oprMatch[1] : (operaMatch ? operaMatch[1] : version);
  }
  // Edge (Chromium-based)
  else if (/Edg\//.test(ua)) {
    name = 'Edge';
    const match = ua.match(/Edg\/([\d.]+)/);
    version = match ? match[1] : version;
  }
  // Chrome
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
    name = 'Chrome';
    const match = ua.match(/Chrome\/([\d.]+)/);
    version = match ? match[1] : version;
  }
  // Safari
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
    name = 'Safari';
    const match = ua.match(/Version\/([\d.]+)/);
    version = match ? match[1] : version;
  }
  // Firefox
  else if (/Firefox\//.test(ua)) {
    name = 'Firefox';
    const match = ua.match(/Firefox\/([\d.]+)/);
    version = match ? match[1] : version;
  }

  return { name, version, platform };
}

/**
 * Checks if the WebAuthn API is supported in the current browser environment
 *
 * This function verifies that the required WebAuthn API is available:
 * - window.PublicKeyCredential (existence)
 * - navigator.credentials.create and .get methods
 *
 * Note: We use a lenient check that works across all platforms including
 * Android browsers where PublicKeyCredential might not be a constructor function.
 *
 * @returns true if WebAuthn is supported, false otherwise
 *
 * @example
 * ```typescript
 * if (!checkWebAuthnSupport()) {
 *   console.error('WebAuthn is not supported in this browser');
 *   return;
 * }
 * // Proceed with WebAuthn operations
 * ```
 */
export function checkWebAuthnSupport(): boolean {
  // More lenient check that works on Android browsers
  // Some Android browsers have PublicKeyCredential as an object/interface
  // rather than a constructor function
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return false;
  }

  // Additionally verify that navigator.credentials API is available
  // This is the actual API we use for create/get operations
  if (typeof navigator === 'undefined' || !navigator.credentials) {
    return false;
  }

  // Check if the essential methods exist
  if (typeof navigator.credentials.create !== 'function' ||
      typeof navigator.credentials.get !== 'function') {
    return false;
  }

  return true;
}

/**
 * Checks if the browser supports specific WebAuthn extensions
 *
 * This function verifies support for optional WebAuthn extensions like
 * PRF (Pseudo-Random Function) and largeBlob.
 *
 * @param _extension - The extension name to check (e.g., 'prf', 'largeBlob')
 * @returns Promise that resolves to true if the extension is supported
 *
 * @example
 * ```typescript
 * const prfSupported = await checkWebAuthnExtensionSupport('prf');
 * if (prfSupported) {
 *   console.log('PRF extension is supported');
 * }
 * ```
 */
export async function checkWebAuthnExtensionSupport(
  _extension: string
): Promise<boolean> {
  if (!checkWebAuthnSupport()) {
    return false;
  }

  try {
    // Check if PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable exists
    if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      // Note: There's no standard way to check extension support yet
      // This is a placeholder for future extension checks
      // For now, we assume extensions might be available if WebAuthn is supported
      // The _extension parameter is reserved for future implementation
      return true;
    }
  } catch (error) {
    // Extension check failed
    return false;
  }

  return false;
}

/**
 * Gets a human-readable description of the current platform
 *
 * @returns A descriptive string about the platform
 *
 * @example
 * ```typescript
 * const description = getPlatformDescription();
 * // Returns: "Chrome 120.0 on iOS"
 * ```
 */
export function getPlatformDescription(): string {
  const info = getBrowserInfo();
  return `${info.name} ${info.version} on ${info.platform}`;
}
